import React from 'react'
import { History } from 'history'
import { ThemeProvider } from 'styled-components'
import { Provider } from 'react-redux'
import { Router } from 'react-router-dom'
import { ApolloProvider } from 'graphql/ApolloProvider'
import store from 'redux/store'

import ConnectedFlagsProvider from 'containers/ConnectedFlagsProvider'
import StripeProvider from 'containers/StripeProvider'
import { LoadingPageProvider } from 'containers/DynamicLoadingPage'
import { EventEmitterProvider } from 'utils'
import ModalProvider from './ModalManager/ModalProvider'
import ToastProvider from './ToastManager/ToastProvider'
import { BannerProvider } from 'containers/Banner'
import { DevToolsSettingsProvider } from 'modules/devtools/settings'

import { GlobalStyle } from 'styles/global'
import theme from 'styles/theme'
import { createLocalStorageSubscription } from 'utils/localStorage'
import { UsageMetricsImport } from 'modules/devtools/usageMetrics/code-split-import'
import ErrorBoundary from 'components/ErrorBoundary'
import GenericErrorView from 'views/GenericErrorView'

interface ProvidersProps {
  history: History
}

// Changes on the authorizations list will refresh non-visible tabs
const localStorageSubscription = createLocalStorageSubscription()
localStorageSubscription.trackStorageChanges('userAllAuthorizations', () => {
  document.hidden && window.location.reload()
})

const Providers: React.FC<ProvidersProps> = ({ children, history }) => (
  <ErrorBoundary>
    <UsageMetricsImport>
      <LoadingPageProvider>
        <EventEmitterProvider>
          <Provider store={store}>
            <ApolloProvider>
              <Router history={history}>
                <DevToolsSettingsProvider>
                  <ConnectedFlagsProvider>
                    <ThemeProvider theme={theme}>
                      <GlobalStyle userAgent={window.navigator.userAgent} />
                      <StripeProvider>
                        <ModalProvider>
                          <BannerProvider>
                            <ErrorBoundary fallbackComponent={GenericErrorView}>
                              <ToastProvider>{children}</ToastProvider>
                            </ErrorBoundary>
                          </BannerProvider>
                        </ModalProvider>
                      </StripeProvider>
                    </ThemeProvider>
                  </ConnectedFlagsProvider>
                </DevToolsSettingsProvider>
              </Router>
            </ApolloProvider>
          </Provider>
        </EventEmitterProvider>
      </LoadingPageProvider>
    </UsageMetricsImport>
  </ErrorBoundary>
)

export default Providers
